import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { freelancerService } from '@/lib/api/services/freelancer.service'
import { FreelancerCard } from '@/components/freelancer/freelancer-card'
import { Search, ArrowLeft, ArrowRight } from 'lucide-react'
import { Input } from '@/components/ui/input'

export default function FeaturedFreelancers() {
  const { userId } = useAuth()
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')

  const { data: freelancers = [], isLoading } = useQuery({
    queryKey: ['featured-freelancers-all', userId],
    queryFn: async () => {
      return await freelancerService.getFeaturedFreelancers()
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })

  const handleSearch = () => {
    const q = searchQuery.trim()
    if (q) {
      navigate(`/client/dashboard?q=${encodeURIComponent(q)}`)
    } else {
      navigate('/client/dashboard')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <div
        className="py-8 sm:py-12 lg:py-16 px-4 sm:px-6 lg:px-8"
        style={{
          background: 'linear-gradient(180deg, rgba(232, 240, 254, 1) 0%, rgba(240, 245, 255, 1) 50%, rgba(248, 250, 252, 1) 100%)',
        }}
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-6 sm:mb-8">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 mb-3 sm:mb-4">
              Featured AI Experts
            </h1>
            <p className="text-base sm:text-lg text-slate-500 leading-relaxed max-w-[600px] mx-auto px-2">
              Discover our hand-picked selection of top-rated AI consultants ready to transform your business
            </p>
          </div>

          {/* Search bar + Back to Find Talent */}
          <div className="max-w-[700px] mx-auto flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => navigate('/client/dashboard')}
              className="flex items-center gap-2 px-5 py-3 rounded-full border-2 border-slate-200 bg-white text-slate-700 font-semibold text-sm hover:bg-slate-50 transition-colors flex-shrink-0"
            >
              <ArrowLeft className="h-4 w-4" />
              Find Talent
            </button>
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search for AI experts..."
                className="h-12 pl-12 pr-28 text-base rounded-full border-2 border-slate-200 focus:border-[#4669ed] focus:ring-0"
              />
              <button
                onClick={handleSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 h-9 px-5 rounded-full bg-[#4669ed] hover:bg-[#3654c9] text-white font-bold text-sm flex items-center gap-1.5 transition-colors"
              >
                Search
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Freelancer Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {isLoading ? (
            // Loading skeleton cards
            Array.from({ length: 6 }).map((_, idx) => (
              <div
                key={idx}
                className="rounded-2xl sm:rounded-[32px] border border-slate-200 p-5 sm:p-7 lg:p-[43px] animate-pulse"
                style={{
                  background: 'linear-gradient(158.39deg, rgba(255, 255, 255, 1) 0%, rgba(248, 250, 255, 1) 100%)',
                }}
              >
                <div className="flex gap-3 sm:gap-4 mb-4">
                  <div className="w-12 h-12 sm:w-[65px] sm:h-[65px] rounded-2xl sm:rounded-[32px] bg-slate-200" />
                  <div className="flex-1 space-y-2">
                    <div className="h-5 sm:h-6 w-3/4 bg-slate-200 rounded" />
                    <div className="h-3 sm:h-4 w-1/2 bg-slate-200 rounded" />
                  </div>
                </div>
                <div className="h-3 sm:h-4 w-full bg-slate-200 rounded mb-2" />
                <div className="h-3 sm:h-4 w-2/3 bg-slate-200 rounded mb-4" />
                <div className="flex gap-2">
                  <div className="h-7 sm:h-8 w-16 sm:w-20 bg-slate-200 rounded-full" />
                  <div className="h-7 sm:h-8 w-12 sm:w-16 bg-slate-200 rounded-full" />
                  <div className="h-7 sm:h-8 w-20 sm:w-24 bg-slate-200 rounded-full" />
                </div>
              </div>
            ))
          ) : freelancers.length === 0 ? (
            <div className="col-span-full text-center py-12 sm:py-16">
              <p className="text-slate-500 text-base sm:text-lg">No featured Geniuses available at the moment.</p>
            </div>
          ) : (
            freelancers.map((freelancer) => (
              <FreelancerCard
                key={freelancer.id || 'unknown'}
                freelancer={freelancer}
                clickable={true}
              />
            ))
          )}
        </div>
      </div>
    </div>
  )
}
